import { useContext, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'
import { Star } from 'lucide-react'
import { FAVORITES_EVENT, saveFavorites } from '../../services/favorites'
import { toList } from '../../utils/collections'

/**
 * L'étoile « mettre cette page en favori » — la même partout : une fois dans
 * l'en-tête (elle vise alors la PAGE COURANTE, donc elle est sur tous les
 * écrans), et par ligne dans une liste (elle vise l'adresse qu'on lui donne).
 *
 * Elle écrit dans le magasin de signets de la page (`dataLocation` →
 * `{favorites: [url…], favorite_labels: {url: nom}}`, cf. userFavoritesProcessor),
 * enregistre (services/favorites.js — qui explique pourquoi l'enregistrement ne
 * peut pas rester un `submitData`), puis ÉMET `favoriteschanged` UNE FOIS LA
 * RÉPONSE REÇUE : le YAML n'a plus qu'à relire le menu, déclarativement.
 *
 * Pourquoi du React ici : rien en YAML ne connaît la route courante d'une SPA
 * ni le titre affiché à l'écran, et rien ne sait écrire une clé DYNAMIQUE (le
 * libellé est rangé à l'URL du favori) — un `setData` a besoin d'un chemin écrit
 * en dur. Le reste (enregistrer, refetcher le menu) est déjà exprimable en YAML,
 * et y reste.
 *
 * Le libellé enregistré est, dans l'ordre : la prop `label`, le titre affiché
 * (premier `<h1>` de la page), `document.title`, à défaut le dernier segment de
 * l'URL. C'est ce que l'utilisateur vient de lire : aucune page n'a à déclarer
 * quoi que ce soit pour être « étoilable ».
 *
 * Ce libellé NOMME AUSSI le bouton (« Ajouter « X » aux favoris ») : une ligne
 * peut porter plusieurs étoiles — le tableau d'un projet et son dashboard sont
 * deux adresses — et deux boutons de même nom accessible seraient impossibles à
 * distinguer au clavier comme au lecteur d'écran.
 *
 * YAML :
 *   - type: FavoriteToggle
 *     dataLocation: ~~.favs           # { favorites, favorite_labels }
 *     url: ~.page_url                 # facultatif (défaut : la page courante)
 *     label: ~.data.name              # facultatif (défaut : le titre à l'écran)
 *     variant: header                 # header | inline
 *     actions:                        # facultatif : relire le menu après coup
 *       - { what: fetchData, on: favoriteschanged, url: /api/orbit/navigation?menu=main, … }
 */
const pageLabel = () => {
  const heading = document.querySelector('main h1, h1')?.textContent?.trim()
  if (heading) return heading
  const title = document.title?.trim()
  if (title && title !== 'Orbit') return title
  const last = window.location.pathname.split('/').filter(Boolean).pop() || ''
  return last ? last.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase()) : 'Page'
}

export const FavoriteToggle = ({ props = {}, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const containerRef = useRef(null)
  const { pathname } = useLocation()
  const resolve = (v) => {
    try {
      return evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })
    } catch {
      return undefined
    }
  }

  const { formData, formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.dataLocation,
    defaultValue: {},
    globalDataContext,
    templateContext,
  })
  // `toList` : sous un `DataFilter`, `favorites` arrive en objet indexé — sans
  // quoi l'étoile s'afficherait éteinte malgré des signets enregistrés.
  const favorites = toList(formData?.favorites)
  const labels = formData?.favorite_labels && typeof formData.favorite_labels === 'object'
    ? formData.favorite_labels
    : {}

  const url = String(resolve(props.url) || pathname || '')
  // Le libellé déclaré sert deux fois : nom du signet, et nom du bouton.
  const subject = String(resolve(props.label) || '')
  const header = resolve(props.variant) !== 'inline'
  const commitEvent = resolve(props.commitEvent) || FAVORITES_EVENT
  const isFavorite = favorites.includes(url)
  if (!url) return null

  const onClick = () => {
    const label = subject || pageLabel()
    const next = isFavorite
      ? { favorites: favorites.filter((u) => u !== url), favorite_labels: labels }
      : { favorites: [...favorites, url], favorite_labels: { ...labels, [url]: label } }
    // Bascule optimiste : l'étoile change tout de suite, l'écriture suit.
    globalDataContext.updateData(next, formDataPath)
    saveFavorites(next).then((saved) => {
      if (saved) globalDataContext.updateData(saved, formDataPath)
      // Enregistré : le menu peut être relu (le classement vient du back).
      containerRef.current?.dispatchEvent(new Event(commitEvent, { bubbles: true }))
    })
  }

  const title = isFavorite
    ? (subject ? `Retirer « ${subject} » des favoris` : 'Retirer des favoris')
    : (subject ? `Ajouter « ${subject} » aux favoris` : 'Ajouter aux favoris')
  return (
    <ActionDependant {...props} attributesHolderRef={containerRef}>
      <button
        ref={containerRef}
        type="button"
        onClick={onClick}
        aria-pressed={isFavorite}
        title={title}
        aria-label={title}
        className={header
          ? 'inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors cursor-pointer hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-iris-400 '
            + (isFavorite ? 'text-amber-400' : 'text-[color:var(--text-dark-muted)] hover:text-white')
          : 'inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors cursor-pointer hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-iris-400 '
            // Éteinte, l'étoile reste un CONTRÔLE : slate-500 (4,5:1 sur blanc)
            // au lieu du slate-300 d'origine, sous le seuil de 3:1 du WCAG 1.4.11.
            + (isFavorite ? 'text-amber-400' : 'text-slate-500 hover:text-amber-400')}
      >
        <Star size={header ? 18 : 17} aria-hidden="true" fill={isFavorite ? 'currentColor' : 'none'} />
      </button>
    </ActionDependant>
  )
}

export default FavoriteToggle
